import React, { useEffect, useMemo } from 'react';
import Select, { ActionMeta, SingleValue } from 'react-select';
import styled from 'styled-components';

export type Category = {
  id: string;
  name: string;
  priority?: string;
};

export type CategorySelectOption = {
  value: string;
  label: string;
};

interface CategorySelectProps {
  categories: Category[];
  selectedCategory: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  placeholder?: string;
  width?: string;
  isDisabled?: boolean;
  defaultCategoryId?: string | null;
}

const CategorySelect: React.FC<CategorySelectProps> = ({
  categories,
  selectedCategory,
  onCategoryChange,
  placeholder = 'Select Category',
  width = '250px',
  isDisabled = false,
  defaultCategoryId = null,
}) => {
  const options = useMemo<CategorySelectOption[]>(
    () => [...categories]
      .map(category => ({ value: category.id, label: category.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [categories]
  );

  useEffect(() => {
    if (!categories || categories.length === 0) {
      onCategoryChange(null);
      return;
    }

    const selectedCategoryExists = categories.some(category => String(category.id) === selectedCategory);
    if (selectedCategoryExists) return;

    const primaryCategory = categories.find(category => category.priority === 'primary');
    const fallbackCategoryId = String(primaryCategory?.id ?? options[0]?.value ?? '');
    const categoryIdToUse = defaultCategoryId ?? fallbackCategoryId;
    onCategoryChange(categoryIdToUse || null);
  }, [categories, selectedCategory, onCategoryChange, options, defaultCategoryId]);

  const selectedOption = options.find(option => option.value === selectedCategory) || null;

  return (
    <StyledSelectContainer width={width}>
      <Select
        options={options}
        placeholder={placeholder}
        value={selectedOption}
        onChange={(option: SingleValue<CategorySelectOption>, _meta: ActionMeta<CategorySelectOption>) =>
          onCategoryChange(option ? option.value : null)
        }
        isDisabled={isDisabled || options.length === 0}
        styles={{
          container: base => ({
            ...base,
            width,
          }),
        }}
      />
    </StyledSelectContainer>
  );
};

const StyledSelectContainer = styled.div<{ width: string }>`
  width: ${({ width }) => width};
`;

export default CategorySelect;
